import { useId, useState } from 'react';
import type { Me } from '../../lib/api.ts';
import { emailLogin, emailRegister } from '../../lib/api.ts';

export interface EmailFormProps {
  readonly onSignedIn: (me: Me) => void;
  /**
   * True when the visitor is currently a guest. Registering then upgrades that
   * account in place, so the copy promises their games come with them — which
   * is only honest in this case.
   */
  readonly claiming: boolean;
}

type Mode = 'register' | 'login';

/**
 * Email sign-in.
 *
 * Deliberately last on the page and collapsed behind a toggle: playing is free
 * and needs no account, so this must never read as a gate. It exists for people
 * who want their rating to follow them to another device.
 */
export function EmailForm({ onSignedIn, claiming }: EmailFormProps) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>('register');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const formId = useId();

  if (!open) {
    return (
      <button type="button" className="button button--quiet" onClick={() => setOpen(true)}>
        Use an email instead
      </button>
    );
  }

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = mode === 'register' ? await emailRegister(email, password) : await emailLogin(email, password);
      if (result.ok) onSignedIn(result.me);
      else setError(result.message);
    } catch {
      setError('Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="emailform" onSubmit={(event) => void submit(event)}>
      <div className="emailform__modes" role="group" aria-label="Email account">
        <button
          type="button"
          className={`emailform__mode${mode === 'register' ? ' emailform__mode--on' : ''}`}
          aria-pressed={mode === 'register'}
          onClick={() => setMode('register')}
        >
          Create account
        </button>
        <button
          type="button"
          className={`emailform__mode${mode === 'login' ? ' emailform__mode--on' : ''}`}
          aria-pressed={mode === 'login'}
          onClick={() => setMode('login')}
        >
          Sign in
        </button>
      </div>

      <label className="emailform__label" htmlFor={`${formId}-email`}>
        Email
      </label>
      <input
        id={`${formId}-email`}
        className="emailform__input"
        type="email"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        autoComplete="email"
        required
      />

      <label className="emailform__label" htmlFor={`${formId}-password`}>
        Password
      </label>
      <input
        id={`${formId}-password`}
        className="emailform__input"
        type="password"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        // Tells a password manager which of the two operations this is, so it
        // offers to save on register and to fill on sign-in.
        autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
        minLength={10}
        required
      />

      {error && (
        <p className="emailform__error" role="alert">
          {error}
        </p>
      )}

      <button type="submit" className="button button--primary" disabled={busy}>
        {busy ? 'Working' : mode === 'register' ? 'Create account' : 'Sign in'}
      </button>

      {mode === 'register' && (
        <p className="emailform__note">
          {claiming
            ? 'Your rating and the games you have played come with you.'
            : 'At least 10 characters.'}
        </p>
      )}
    </form>
  );
}
